import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PRODUCTION, STAGING, VALIDATION, INTERCOM_APP_ID } from 'constants';
import ExternalServicesContext from 'contexts/external_services/context';
import get from 'lodash/get';
import { initTracker } from 'services/tracer';
import * as Sentry from '@sentry/react';
import Intercom, { show, update, shutdown } from '@intercom/messenger-js-sdk';
import { identifyHyperDX } from 'services/hyperdx';
import { initOpenPanel, identifyOpenPanel, trackPageOpenPanel, trackOpenPanel } from 'services/openpanel';
initTracker();
initOpenPanel();

const ExternalServicesProvider = ({ children }) => {
  const currentUserRef = useRef(null);

  const identifyUser = (user, isProvider) => {
    currentUserRef.current = {
      ...user,
      isProvider,
    };
    if (STAGING || VALIDATION || PRODUCTION) {
      update({
        user_id: user.id,
        name: user.display_name,
        company: {
          id: user.account.id,
          name: user.account.name,
          created_at: user.account.created_at,
        },
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        display_name: user.display_name,
        created_at: user.created_at,
        language_override: user.locale,
        account_name: user.account.name,
        user_hash: user.intercom_hash,
      });
    }
    identifyHyperDX(user);
    identifyOpenPanel(user);
  };

  const openIntercom = () => {
    show();
  };

  const trackPage = (path) => {
    trackPageOpenPanel(path);
  };

  const trackEvent = (event, properties) => {
    trackOpenPanel(event, properties);
  };

  const updateIntercom = () => {
    if (Intercom !== undefined) {
      update();
    }
  };

  const trackIssue = (issue) => {
    Sentry.captureException(issue, {
      user_id: get(currentUserRef.current, 'id'),
      user_email: get(currentUserRef.current, 'email'),
      user_display_name: get(currentUserRef.current, 'display_name'),
      role: get(currentUserRef.current, 'isProvider', false) ? 'provider' : 'seller',
    });
  };

  const closeIntercom = () => {
    shutdown();
  };

  useEffect(() => {
    Intercom({
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: true,
    });
  }, []);

  return (
    <ExternalServicesContext.Provider
      value={{
        identifyUser,
        updateIntercom,
        openIntercom,
        trackPage,
        trackEvent,
        trackIssue,
        closeIntercom,
      }}
    >
      {children}
    </ExternalServicesContext.Provider>
  );
};

ExternalServicesProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]).isRequired,
};

export default ExternalServicesProvider;
